/**
 * The invite-only welcome on the signed-out home (GH-871b; recomposed by GH-879).
 *
 * Owner, 2026-10-04: *"we need a special home screen for invite only period where the most
 * prominent thing is the invite code entry dialog and below it is option to join waitlist. Above
 * that we can also have Logo, app name and tag line"* — the iPhone's welcome, said the same way
 * here. While membership is on and nobody is signed in, `Home` draws this in place of its title:
 *
 *  1. the lockup — GiveHere's eight-point star as a brass keyline, the name, and the tag line;
 *  2. "Invite only" over the access-code field and Continue, the page's one filled button;
 *  3. "No code? Join the waitlist", the quiet outlined button, and "Have an account? Sign in";
 *  4. "You can still give without an account." — which, on the web, leads into the directory below,
 *     because the directory is this page's only way to an organization and giving is never behind
 *     a code (DEC-052). The bar's "Check in" stays where it is: it is the web's way in with an
 *     event's code, as the scanner is on the iPhone.
 *
 * ## Continue
 *
 * A code that cannot be a code is never sent. A well-formed one is looked up
 * (`POST /v1/public/invites/lookup`, the code in the body); unknown, used, revoked and expired all
 * read the same — "This invite isn't available" — so the card cannot be used to probe codes. An
 * available one says who sent it ("Amina R. invited you", or "You're invited") and opens the
 * sign-in sheet with the code carried as `inviteCode` on every door, so the account is made with
 * the invite. An existing account that signs in with a code redeems it afterwards, as `/i/<code>`
 * does. Closing the sheet spends nothing: the code is still held, and Continue opens it again.
 */
import { useState } from "react";
import {
  ACCESS_COPY,
  inviteCodeSchema,
  MEMBERSHIP_COPY,
  type DonorSessionResponse,
  type PublicInviteResponse,
} from "@givehere/contracts";
import { fetchPublicInvite } from "./api.js";
import { redeemInvite } from "./donor-session.js";
import { ALREADY_MEMBER, redeemFailure } from "./InviteCodeForm.js";
import { SignInSheet } from "./SignInSheet.js";

const invitedLine = (invite: PublicInviteResponse): string =>
  invite.inviterName
    ? MEMBERSHIP_COPY.invitedYou(invite.inviterName)
    : MEMBERSHIP_COPY.invitedYouAnonymous;

/**
 * Lantern's eight-point star — the app icon's and iOS `LatticeMark`'s drawing, the same path the
 * page's lattice band masks with — stroked in brass: identity, never a control.
 */
function GiveHereMark() {
  return (
    <svg
      className="home-welcome__mark"
      viewBox="-0.5 -0.5 13 13"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12.00,6.00 L10.24,7.76 L10.24,10.24 L7.76,10.24 L6.00,12.00 L4.24,10.24 L1.76,10.24 L1.76,7.76 L0.00,6.00 L1.76,4.24 L1.76,1.76 L4.24,1.76 L6.00,0.00 L7.76,1.76 L10.24,1.76 L10.24,4.24Z M10.26,6.00 L9.01,7.25 L9.01,9.01 L7.25,9.01 L6.00,10.26 L4.75,9.01 L2.99,9.01 L2.99,7.25 L1.74,6.00 L2.99,4.75 L2.99,2.99 L4.75,2.99 L6.00,1.74 L7.25,2.99 L9.01,2.99 L9.01,4.75Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** The welcome's head: the mark, the name (the page's one heading), and the tag line. */
export function WelcomeLockup() {
  return (
    <div className="home-welcome__lockup">
      <GiveHereMark />
      <h1 className="home__title">GiveHere</h1>
      <p className="home__subtitle">{ACCESS_COPY.home.tagline}</p>
    </div>
  );
}

export function HomeInviteCard({
  onSignIn,
  onIssued,
  onNotice,
}: {
  /** "Have an account? Sign in": the home's own sign-in door. */
  onSignIn: () => void;
  /** A session from the sheet; the home takes it as from any door. */
  onIssued: (issued: DonorSessionResponse) => void;
  /** A redeem that failed after an existing account signed in, for the home's notice line. */
  onNotice: (message: string) => void;
}) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<{ code: string; invite: PublicInviteResponse } | null>(null);
  const [signingUp, setSigningUp] = useState(false);

  const unavailable = () => {
    setFound(null);
    setError(MEMBERSHIP_COPY.inviteUnavailable);
  };

  const submit = async () => {
    setError(null);
    const parsed = inviteCodeSchema.safeParse(raw);
    if (!parsed.success) {
      unavailable();
      return;
    }
    // The same code, already found good: the sheet again, with nothing asked twice.
    if (found && found.code === parsed.data) {
      setSigningUp(true);
      return;
    }
    setBusy(true);
    try {
      const invite = await fetchPublicInvite(parsed.data);
      if (invite === null || invite.status !== "available") {
        unavailable();
        return;
      }
      setFound({ code: parsed.data, invite });
      setSigningUp(true);
    } catch {
      setError("That didn’t work. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const afterSignIn = async (issued: DonorSessionResponse, code: string) => {
    setSigningUp(false);
    // A new account was made with the invite; an existing one redeems it now, as `/i/` does.
    if (!issued.membershipJoined) {
      await redeemInvite(issued.token, code).catch((caught: unknown) => {
        const message = redeemFailure(caught);
        if (message !== ALREADY_MEMBER) onNotice(message);
      });
    }
    onIssued(issued);
  };

  return (
    <div className="home-welcome">
      <section className="home-invite" aria-labelledby="home-invite-title">
        <h2 id="home-invite-title" className="home-invite__title">
          {ACCESS_COPY.home.title}
        </h2>
        {found && (
          <p className="home-invite__inviter" role="status" dir="auto">
            {invitedLine(found.invite)}
          </p>
        )}
        <form
          className="invite-form"
          onSubmit={(submitted) => {
            submitted.preventDefault();
            void submit();
          }}
        >
          <label className="field">
            <span>{ACCESS_COPY.home.codeLabel}</span>
            <input
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={32}
              value={raw}
              aria-invalid={error === MEMBERSHIP_COPY.inviteUnavailable || undefined}
              aria-describedby={error ? "home-invite-error" : undefined}
              onChange={(changed) => setRaw(changed.target.value)}
            />
          </label>
          {error && (
            <p id="home-invite-error" className="notice notice--error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="pay" disabled={busy || raw.trim().length === 0}>
            {busy ? "Checking…" : ACCESS_COPY.home.continue}
          </button>
        </form>
      </section>

      <a className="pay pay--secondary landing__give home-welcome__waitlist" href="/waitlist">
        {ACCESS_COPY.home.waitlist}
      </a>
      <button type="button" className="link home-welcome__sign-in" onClick={onSignIn}>
        {ACCESS_COPY.home.signIn}
      </button>
      <p className="home-invite__note">{ACCESS_COPY.home.giveNote}</p>

      {signingUp && found && (
        <SignInSheet
          heading={invitedLine(found.invite)}
          lede={ACCESS_COPY.home.signUpLede}
          inviteCode={found.code}
          onIssued={(issued) => afterSignIn(issued, found.code)}
          onDismiss={() => setSigningUp(false)}
        />
      )}
    </div>
  );
}
